/**
 * India Standard Time (Asia/Kolkata) for all admin dashboard dates/times.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const IST = 'Asia/Kolkata';

/** True when the string carries an explicit timezone (Z or ±hh:mm) → a real instant. */
function hasExplicitTz(raw) {
  const s = String(raw).trim();
  return /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
}

/** Render a timezone-aware instant in IST. Returns null if unparseable. */
function formatInstantIst(raw, withTime) {
  const d = new Date(String(raw).trim());
  if (Number.isNaN(d.getTime())) return null;
  const opts = withTime
    ? { timeZone: IST, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }
    : { timeZone: IST, day: '2-digit', month: 'short', year: 'numeric' };
  return `${d.toLocaleString('en-IN', opts)} IST`;
}

function parseWallClock(value) {
  if (value == null || value === '') return null;
  // Never treat an explicit-timezone instant as wall clock.
  if (hasExplicitTz(value)) return null;
  const raw = String(value).trim().replace('T', ' ');
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return null;
  return {
    year: m[1],
    month: MONTHS[parseInt(m[2], 10) - 1],
    day: m[3],
    hour24: m[4] != null ? parseInt(m[4], 10) : null,
    minute: m[5] != null ? m[5] : null,
  };
}

function formatWallDate(wall, withTime) {
  if (!wall) return '—';
  if (withTime && wall.hour24 != null) {
    const h = wall.hour24 % 12 || 12;
    const ampm = wall.hour24 >= 12 ? 'PM' : 'AM';
    return `${wall.day} ${wall.month} ${wall.year}, ${h}:${wall.minute} ${ampm} IST`;
  }
  return `${wall.day} ${wall.month} ${wall.year} IST`;
}

/** Current calendar date in IST as YYYY-MM-DD (form defaults). */
export function istToday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Current year-month in IST as YYYY-MM. */
export function istYearMonth() {
  const parts = {};
  new Intl.DateTimeFormat('en-CA', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
  })
    .formatToParts(new Date())
    .forEach((p) => {
      parts[p.type] = p.value;
    });
  return `${parts.year}-${parts.month}`;
}

/** Date + time for tables and detail views. */
export function formatIstDateTime(value) {
  if (value == null || value === '') return '—';
  // Timezone-aware instant (e.g. ISO with Z or +hh:mm): convert UTC→IST.
  if (hasExplicitTz(value)) {
    return formatInstantIst(value, true) || '—';
  }
  // Plain string with no timezone: treat as IST wall-clock (server already IST).
  const wall = parseWallClock(value);
  if (wall) return formatWallDate(wall, wall.hour24 != null);
  return '—';
}

/** Date only (order date, payment date). */
export function formatIstDate(value) {
  if (value == null || value === '') return '—';
  // Timezone-aware instant: resolve the calendar date in IST.
  if (hasExplicitTz(value)) {
    return formatInstantIst(value, false) || '—';
  }
  const wall = parseWallClock(value);
  if (wall) return formatWallDate(wall, false);
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw.slice(0, 10))) {
    const [y, mo, d] = raw.slice(0, 10).split('-');
    return `${d} ${MONTHS[parseInt(mo, 10) - 1]} ${y} IST`;
  }
  return formatIstDateTime(value);
}

/** Month label e.g. "May 2026 IST" from YYYY-MM or datetime. */
export function formatIstMonthYear(value) {
  if (value == null || value === '') return '—';
  const ym = String(value).trim().slice(0, 7);
  const m = ym.match(/^(\d{4})-(\d{2})$/);
  if (m) {
    return `${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]} IST`;
  }
  return formatIstDateTime(value);
}

/** First day of current month in IST (sales forms). */
export function istMonthFirst() {
  return `${istYearMonth()}-01`;
}

/** Normalize YYYY-MM, YYYY-MM-DD, or datetime to YYYY-MM. */
export function normalizeYearMonth(value) {
  if (value == null || value === '') return '';
  return String(value).trim().slice(0, 7);
}

/** Volume in m³ — preserve decimals (up to 2). */
export function formatVolumeM3(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/** ISO-8601 with +05:30 for API payloads. */
export function istNowIso8601() {
  const parts = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(new Date())
    .forEach((p) => {
      if (p.type !== 'literal') parts[p.type] = p.value;
    });
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}+05:30`;
}

/** "Now" for refresh labels. */
export function formatIstNow() {
  return formatIstDateTime(istNowIso8601());
}
